import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUserRole, deleteUser, resetUserPassword, getAnalystRoles } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, 
  Trash2, 
  Shield,
  ShieldCheck,
  Clock,
  Mail,
  Plus,
  Loader2,
  Key,
  Brain
} from 'lucide-react';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { user: currentUser } = useAuth();
  
  // Analyst roles
  const [analystRoles, setAnalystRoles] = useState({});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer',
    analyst_role: 'data_scientist'
  });

  useEffect(() => {
    fetchUsers();
    fetchAnalystRoles();
  }, []);
  
  const fetchAnalystRoles = async () => {
    try {
      const data = await getAnalystRoles();
      setAnalystRoles(data.roles || {});
    } catch (error) {
      console.error('Failed to fetch analyst roles:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const newUser = await createUser(formData);
      setUsers(prev => [...prev, newUser]);
      setDialogOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'viewer', analyst_role: 'data_scientist' });
      toast.success('User created successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create user';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await resetUserPassword(selectedUserId, newPassword);
      toast.success('Password reset successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUserId(null);
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const openPasswordDialog = (userId) => {
    setSelectedUserId(userId);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      toast.success('Role updated successfully');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            User Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage team members and their access levels
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Create New User</DialogTitle>
              <DialogDescription className="text-gray-500">
                Add a new team member to the platform
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="user@company.com"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="viewer" className="text-gray-700 focus:bg-gray-50">
                      Viewer - Read-only access
                    </SelectItem>
                    <SelectItem value="admin" className="text-gray-700 focus:bg-gray-50">
                      Admin - Full access
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Analyst Role - Only show for non-admin users */}
              {formData.role !== 'admin' && (
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Analyst Perspective
                  </Label>
                  <Select 
                    value={formData.analyst_role} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, analyst_role: value }))}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900" data-testid="user-analyst-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {Object.entries(analystRoles).filter(([id]) => id !== 'all').map(([roleId, role]) => (
                        <SelectItem key={roleId} value={roleId} className="text-gray-700 focus:bg-gray-50">
                          <div className="flex items-center gap-2">
                            <span>{role.icon}</span>
                            <span>{role.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    This determines the AI's analysis perspective for this user. Admins have access to all perspectives.
                  </p>
                </div>
              )}
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-500">
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} className="btn-gold" data-testid="save-user-btn">
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[#D4AF37]/10">
              <Users className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {users.length}
              </p>
              <p className="text-xs text-gray-500">Total Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-100">
              <ShieldCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {users.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-xs text-gray-500">Admins</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gray-100">
              <Shield className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {users.filter(u => u.role === 'viewer').length}
              </p>
              <p className="text-xs text-gray-500">Viewers</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-900">Team Members</CardTitle>
          <CardDescription className="text-gray-500">
            Manage user roles and permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Avatar className="w-10 h-10 border border-gray-200">
                    <AvatarFallback className="bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-medium">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-gray-900 font-medium">{user.name}</p>
                      {user.id === currentUser?.id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37]">
                          You
                        </span>
                      )}
                      {/* Analyst Role Badge */}
                      {user.role !== 'admin' && user.analyst_role && analystRoles[user.analyst_role] && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-indigo-50 text-indigo-700 border-indigo-200">
                          {analystRoles[user.analyst_role]?.icon} {analystRoles[user.analyst_role]?.name}
                        </Badge>
                      )}
                      {user.role === 'admin' && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-amber-50 text-amber-700 border-amber-200">
                          🎯 All Perspectives
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span>{user.email}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Joined {formatDate(user.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Select
                    value={user.role}
                    onValueChange={(value) => handleRoleChange(user.id, value)}
                    disabled={user.id === currentUser?.id}
                  >
                    <SelectTrigger 
                      className={`w-[120px] ${
                        user.role === 'admin' 
                          ? 'bg-amber-50 border-amber-200 text-amber-700' 
                          : 'bg-gray-50 border-gray-200 text-gray-600'
                      }`}
                      data-testid={`role-select-${user.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem 
                        value="admin" 
                        className="text-amber-700 focus:bg-amber-50"
                      >
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4" />
                          Admin
                        </div>
                      </SelectItem>
                      <SelectItem 
                        value="viewer"
                        className="text-gray-600 focus:bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Viewer
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {user.id !== currentUser?.id && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPasswordDialog(user.id)}
                        className="text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-gray-200">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-gray-900">Delete User?</AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-500">
                              This will permanently delete {user.name}'s account. They will lose access to the platform immediately.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-gray-200 text-gray-700 hover:bg-gray-50">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(user.id)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              Delete User
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Info */}
      <Card className="bg-gradient-to-br from-[#D4AF37]/5 to-[#B8962E]/10 border-[#D4AF37]/10">
        <CardContent className="p-6">
          <h3 className="text-gray-900 font-semibold mb-4">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-amber-600" />
                <span className="text-gray-900 font-medium">Admin</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 ml-6">
                <li>• Full access to all features</li>
                <li>• Manage database connections</li>
                <li>• Add/remove users</li>
                <li>• Change user roles</li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="text-gray-900 font-medium">Viewer</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 ml-6">
                <li>• Run AI queries</li>
                <li>• View query history</li>
                <li>• Change own password</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5 text-[#D4AF37]" />
              Reset User Password
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Set a new password for this user
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-700">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-gray-50 border-gray-200 text-gray-900"
                required
                minLength={6}
                data-testid="reset-password-input"
              />
              <p className="text-xs text-gray-500">Minimum 6 characters</p>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setPasswordDialogOpen(false)} className="text-gray-500">
                Cancel
              </Button>
              <Button type="submit" disabled={resetting} className="btn-gold" data-testid="confirm-reset-btn">
                {resetting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
